import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Coins, Calendar, Flame, Users,
  LogOut, Pencil, Camera, ImagePlus, Settings,
} from "lucide-react";
import { FanLevelBadge } from "@/components/FanLevelBadge";
import { ProfileCompletionBar } from "@/components/ProfileCompletionBar";
import { ProfileOverviewTab } from "@/components/profile/ProfileOverviewTab";
import { ProfileSocialTab } from "@/components/profile/ProfileSocialTab";
import { ProfileAuctionsTab } from "@/components/profile/ProfileAuctionsTab";
import { ProfileEditModal } from "@/components/profile/ProfileEditModal";
import { SOCIAL_PLATFORMS } from "@/config/socialPlatforms";
import { toast } from "sonner";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.4, ease: "easeOut" } }),
};

export default function ProfilePage() {
  const { user, api, logout, streak, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [creditHistory, setCreditHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [referral, setReferral] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [applyingCode, setApplyingCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [myClaims, setMyClaims] = useState([]);
  const [followedArtists, setFollowedArtists] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const coverInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const completionRef = useRef(null);

  useEffect(() => {
    if (showHistory) {
      api.get("/credits/history", { params: { limit: 20 } })
        .then((res) => setCreditHistory(res.data.transactions))
        .catch(console.error);
    }
  }, [showHistory, api]);

  useEffect(() => {
    Promise.all([
      api.get("/referral/me").catch(() => ({ data: null })),
      api.get("/rewards/my-claims").catch(() => ({ data: { claims: [] } })),
      api.get("/auth/followed-artists").catch(() => ({ data: { artists: [] } })),
      api.get("/auth/activity").catch(() => ({ data: { activities: [] } })),
    ]).then(([refRes, claimsRes, followRes, activityRes]) => {
      if (refRes.data) setReferral(refRes.data);
      setMyClaims(claimsRes.data.claims || []);
      setFollowedArtists(followRes.data.artists || []);
      setRecentActivity(activityRes.data.activities || []);
    });
  }, [api]);

  const startEditing = () => {
    setEditForm({
      pseudo: user.pseudo || "",
      bio: user.bio || "",
      city: user.city || "",
      genres: user.genres || [],
      social_links: user.social_links || {},
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", editForm);
      await refreshUser();
      setEditing(false);
      completionRef.current?.refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await api.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put("/auth/profile", { avatar_url: uploadRes.data.url });
      await refreshUser();
      completionRef.current?.refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    }
    setAvatarUploading(false);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await api.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put("/auth/profile", { cover_url: uploadRes.data.url });
      await refreshUser();
      completionRef.current?.refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    }
    setCoverUploading(false);
  };

  const copyCode = () => {
    if (!referral?.code) return;
    const link = `${window.location.origin}/?ref=${referral.code}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareCode = () => {
    if (!referral?.code) return;
    const link = `${window.location.origin}/?ref=${referral.code}`;
    const text = `Rejoins ReallyFan et gagne ${referral.referred_bonus} credits gratuits ! Utilise mon code : ${referral.code}`;
    if (navigator.share) {
      navigator.share({ title: "ReallyFan", text, url: link });
    } else {
      copyCode();
    }
  };

  const applyReferral = async () => {
    if (!referralCode.trim()) return;
    setApplyingCode(true);
    try {
      const res = await api.post("/referral/apply", { code: referralCode.trim() });
      toast.success(`+${res.data.bonus} credits ! Parraine par ${res.data.referrer_pseudo}`);
      refreshUser();
      setReferralCode("");
      api.get("/referral/me").then((r) => setReferral(r.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
    } finally {
      setApplyingCode(false);
    }
  };

  if (!user) return null;

  const joinDate = new Date(user.created_at).toLocaleDateString("fr-FR", {
    year: "numeric", month: "long", day: "numeric"
  });

  const socialLinks = user.social_links || {};
  const hasSocialLinks = Object.values(socialLinks).some((v) => v);

  const tabs = [
    { id: "overview", label: "Apercu" },
    { id: "social", label: "Social" },
    { id: "auctions", label: "Encheres" },
  ];

  return (
    <div className="py-6 space-y-5" data-testid="profile-page">
      {/* Hero / Cover Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative rounded-3xl overflow-hidden shadow-lg"
      >
        {/* Cover Image */}
        <div
          className="h-44 bg-gradient-to-br from-[#FF6B35]/40 via-[#7000FF]/30 to-[#0F0A1F] relative group cursor-pointer"
          onClick={() => coverInputRef.current?.click()}
          data-testid="profile-cover-area"
        >
          {user.cover_url && (
            <img src={user.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 text-white text-xs font-bold">
              {coverUploading ? "Upload..." : <><ImagePlus className="w-4 h-4" /> Changer la banniere</>}
            </div>
          </div>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleCoverUpload}
            disabled={coverUploading}
            data-testid="cover-upload-input"
          />
        </div>

        {/* Profile Info Overlay */}
        <div className="bg-card border border-border px-5 pb-5 pt-14 relative">
          {/* Avatar - positioned to overlap banner */}
          <div className="absolute -top-10 left-5 z-10" data-testid="profile-avatar-container">
            <div className="relative group">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center border-4 border-card shadow-xl overflow-hidden">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-heading font-bold text-3xl">
                    {(user.pseudo || "?")[0].toUpperCase()}
                  </span>
                )}
              </div>
              <button
                onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/0 group-hover:bg-black/40 transition-colors"
                data-testid="avatar-upload-btn"
              >
                <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
                disabled={avatarUploading}
                data-testid="avatar-upload-input"
              />
              {avatarUploading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/50">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Name & Info - positioned after avatar space */}
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-xl text-card-foreground truncate" data-testid="profile-pseudo">
                {user.pseudo}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${
                    user.role === "artist"
                      ? "bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/30"
                      : "bg-[#7000FF]/10 text-[#7000FF] border-[#7000FF]/30"
                  }`}
                  data-testid="profile-role-badge"
                >
                  {user.role === "artist" ? "Artiste" : "Fan"}
                </span>
                <FanLevelBadge compact />
                {user.city && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" /> {user.city}
                  </span>
                )}
              </div>
            </div>

            {/* Edit Button */}
            <button
              onClick={startEditing}
              className="p-2.5 rounded-xl bg-muted text-muted-foreground hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all shrink-0"
              data-testid="edit-profile-btn"
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>

          {/* Bio */}
          {user.bio && (
            <p className="text-sm text-card-foreground/80 mt-3 leading-relaxed">{user.bio}</p>
          )}

          {/* Genres */}
          {user.genres?.length > 0 && (
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {user.genres.map((g) => (
                <span key={g} className="text-[10px] px-2.5 py-1 rounded-full bg-[#FF6B35]/8 text-[#FF6B35] border border-[#FF6B35]/20 font-medium">
                  {g}
                </span>
              ))}
            </div>
          )}

          {/* Social Links */}
          {hasSocialLinks && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {SOCIAL_PLATFORMS.map(({ key, icon: Icon, label, url }) => {
                const val = socialLinks[key];
                if (!val) return null;
                return (
                  <a
                    key={key}
                    href={url(val)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={label}
                    className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all"
                    data-testid={`social-link-${key}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </a>
                );
              })}
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            <div
              className="bg-white rounded-2xl p-2.5 text-center border border-border cursor-pointer hover:border-[#FF6B35]/30 transition-colors"
              onClick={() => navigate("/credits")}
              data-testid="stat-credits"
            >
              <Coins className="w-4 h-4 text-[#FF6B35] mx-auto mb-0.5" />
              <p className="font-mono-credits font-bold text-base text-card-foreground" data-testid="profile-credits">
                {user.credits_balance}
              </p>
              <p className="text-[9px] text-muted-foreground">Credits</p>
            </div>
            <div className="bg-white rounded-2xl p-2.5 text-center border border-border" data-testid="stat-streak">
              <Flame
                className={`w-4 h-4 mx-auto mb-0.5 ${(streak?.streak_count || 0) > 0 ? "text-orange-400" : "text-muted-foreground"}`}
                fill={(streak?.streak_count || 0) > 0 ? "currentColor" : "none"}
                strokeWidth={(streak?.streak_count || 0) > 0 ? 0 : 1.5}
              />
              <p className="font-mono-credits font-bold text-base text-card-foreground" data-testid="profile-streak">
                {streak?.streak_count || 0}
              </p>
              <p className="text-[9px] text-muted-foreground">Streak</p>
            </div>
            <div className="bg-white rounded-2xl p-2.5 text-center border border-border" data-testid="stat-follows">
              <Users className="w-4 h-4 text-[#7000FF] mx-auto mb-0.5" />
              <p className="font-mono-credits font-bold text-base text-card-foreground">{followedArtists.length}</p>
              <p className="text-[9px] text-muted-foreground">Suivis</p>
            </div>
            <div className="bg-white rounded-2xl p-2.5 text-center border border-border" data-testid="stat-joined">
              <Calendar className="w-4 h-4 text-[#FF8FA3] mx-auto mb-0.5" />
              <p className="text-[10px] font-medium text-card-foreground leading-tight">{joinDate.split(" ").slice(0, 2).join(" ")}</p>
              <p className="text-[9px] text-muted-foreground">Membre</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editing && (
          <ProfileEditModal
            editForm={editForm}
            setEditForm={setEditForm}
            saving={saving}
            saveProfile={saveProfile}
            onCancel={() => setEditing(false)}
          />
        )}
      </AnimatePresence>

      {/* Streak Detail */}
      {(streak?.streak_count || 0) > 0 && (
        <motion.div
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-3xl p-4"
          data-testid="streak-detail"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <Flame className="w-4 h-4 text-orange-500" fill="currentColor" strokeWidth={0} />
              </div>
              <div>
                <span className="font-heading font-bold text-sm text-orange-600">
                  {streak.streak_count} jour{streak.streak_count > 1 ? "s" : ""}
                </span>
                <p className="text-[10px] text-orange-400">Record : {streak.streak_best || streak.streak_count}</p>
              </div>
            </div>
            <span className="text-xs text-orange-600 font-mono-credits font-bold">
              +{5 * streak.streak_count} XP
            </span>
          </div>
          <div className="flex gap-1 mt-1">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <div
                key={d}
                className={`flex-1 h-2 rounded-full transition-all ${
                  d <= (streak.streak_count % 7 || (streak.streak_count > 0 && streak.streak_count % 7 === 0 ? 7 : 0))
                    ? "bg-gradient-to-r from-orange-400 to-amber-400"
                    : "bg-orange-100"
                }`}
              />
            ))}
          </div>
          <p className="text-[10px] text-orange-500/80 mt-2 text-center">
            {streak.streak_count % 7 === 0
              ? "Bonus +50 credits debloque !"
              : `Encore ${7 - (streak.streak_count % 7)} jour${7 - (streak.streak_count % 7) > 1 ? "s" : ""} pour le bonus +50`}
          </p>
        </motion.div>
      )}

      {/* Profile Completion Bar */}
      <ProfileCompletionBar ref={completionRef} onEdit={() => setEditing(true)} />

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-muted rounded-2xl p-1" data-testid="profile-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
              activeTab === tab.id
                ? "bg-white text-card-foreground shadow-sm"
                : "text-muted-foreground hover:text-card-foreground/80"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <ProfileOverviewTab
            user={user}
            followedArtists={followedArtists}
            recentActivity={recentActivity}
          />
        )}

        {activeTab === "social" && (
          <ProfileSocialTab
            user={user}
            referral={referral}
            referralCode={referralCode}
            setReferralCode={setReferralCode}
            applyingCode={applyingCode}
            applyReferral={applyReferral}
            copied={copied}
            copyCode={copyCode}
            shareCode={shareCode}
            showHistory={showHistory}
            setShowHistory={setShowHistory}
            creditHistory={creditHistory}
          />
        )}

        {activeTab === "auctions" && (
          <ProfileAuctionsTab user={user} myClaims={myClaims} />
        )}
      </AnimatePresence>

      {/* Footer Links */}
      <div className="flex gap-3">
        <button
          onClick={() => navigate("/settings")}
          className="flex-1 text-center text-xs text-muted-foreground hover:text-[#FF6B35] transition-colors py-2.5 bg-card border border-border rounded-2xl flex items-center justify-center gap-1.5"
          data-testid="settings-link"
        >
          <Settings className="w-3.5 h-3.5" /> Parametres
        </button>
        <button
          onClick={() => navigate("/help")}
          className="flex-1 text-center text-xs text-muted-foreground hover:text-[#FF6B35] transition-colors py-2.5 bg-card border border-border rounded-2xl"
          data-testid="help-link"
        >
          Aide & FAQ
        </button>
      </div>

      {/* Logout */}
      <button
        onClick={() => { logout(); navigate("/"); }}
        className="w-full bg-card border border-red-100 rounded-3xl p-4 flex items-center justify-center gap-2 text-muted-foreground hover:text-red-500 hover:border-red-300 hover:bg-red-50/50 transition-all duration-200 shadow-sm"
        data-testid="profile-logout-btn"
      >
        <LogOut className="w-4 h-4" />
        Se deconnecter
      </button>
    </div>
  );
}
